import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useHintDismiss, DismissScope } from "../hooks/useHintDismiss";

type Severity = "info" | "success" | "tip";

interface Props {
  /** Stable key; never rename once shipped (dismissals keyed by this). */
  hintKey: string;
  title: string;
  body?: React.ReactNode;
  severity?: Severity;
  /** Optional CTA. If `to` is set → internal Link. If `href` → external. */
  cta?: { label: string; to?: string; href?: string; onClick?: () => void };
  /** If set, also show a "why am I seeing this?" disclosure line. */
  why?: string;
  className?: string;
}

const STYLES: Record<Severity, { border: string; bg: string; dot: string; text: string; sub: string }> = {
  info:    { border: "border-blue-300",  bg: "bg-blue-50",   dot: "bg-blue-400",   text: "text-blue-800",   sub: "text-blue-600" },
  success: { border: "border-emerald-300", bg: "bg-emerald-50", dot: "bg-emerald-500", text: "text-emerald-800", sub: "text-emerald-600" },
  tip:     { border: "border-violet-300", bg: "bg-violet-50",  dot: "bg-violet-400",  text: "text-violet-800",  sub: "text-violet-600" },
};

export default function FeatureHint({
  hintKey, title, body, severity = "tip", cta, why, className = ""
}: Props) {
  const { isDismissed, dismiss } = useHintDismiss();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  if (isDismissed(hintKey)) return null;

  const s = STYLES[severity];
  const handleDismiss = (scope: DismissScope) => {
    dismiss(hintKey, scope);
    setMenuOpen(false);
  };

  const renderCta = () => {
    if (!cta) return null;
    const cls = "text-xs font-medium underline underline-offset-2 hover:opacity-80";
    if (cta.to) return <Link to={cta.to} className={`${s.text} ${cls}`}>{cta.label}</Link>;
    if (cta.href) return <a href={cta.href} target="_blank" rel="noreferrer" className={`${s.text} ${cls}`}>{cta.label}</a>;
    return <button onClick={cta.onClick} className={`${s.text} ${cls}`}>{cta.label}</button>;
  };

  return (
    <div
      className={`rounded-lg px-4 py-3 border ${s.border} ${s.bg} flex flex-wrap items-start gap-3 ${className}`}
      role="status"
    >
      <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.dot}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${s.text}`}>{title}</div>
        {body && <div className={`text-xs mt-1 ${s.sub}`}>{body}</div>}
        <div className="flex flex-wrap items-center gap-3 mt-2">
          {renderCta()}
          {why && <span className={`text-[11px] ${s.sub}`}>{why}</span>}
        </div>
      </div>

      <div ref={menuRef} className="relative shrink-0">
        <button
          onClick={() => setMenuOpen(v => !v)}
          className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-500 hover:bg-white"
          aria-label="Dismiss"
        >
          Dismiss ▾
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
            {([["1h", "Snooze 1 hour"], ["24h", "Snooze 1 day"], ["permanent", "Don't show this again"]] as const).map(([scope, label]) => (
              <button key={scope} onClick={() => handleDismiss(scope)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700">
                {label}
              </button>
            ))}
            <div className="border-t border-slate-100">
              <button onClick={() => handleDismiss("suppress-forever")}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 text-slate-500">
                Never show feature tips (global)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
